/**
 * Phase 5: Safe "For You" Feed Rules — PRODUCTION TIGHTENED
 *
 * Eligibility: canonical public-live + quality≥40, spam<50, no under-review/flagged
 * Ranking: personalization×0.35 + quality×0.20 + trust×0.15 + freshness×0.15 + trending×0.10 - penalties
 * Diversity: max 2/seller in first 12, max 3 consecutive same category
 * Explainability: reason tags only from verified real signals
 *
 * iter316 — Status check now uses the canonical `isLiveListing` predicate
 * so For You agrees with /api/ads (real ads ship `status: "active"`) and
 * doesn't rely on the Home.js fallback to mask the older `=== "published"`
 * mismatch from iter315.
 */

import { scoreListingForYou, hasEnoughSignals } from './forYou.js';
import { isLiveListing } from './liveListing.js';

// ============================================================
// Normalize raw score to 0-1 range
// ============================================================
function norm(raw, maxExpected) {
  return Math.min(1, Math.max(0, (raw || 0) / maxExpected));
}

// ============================================================
// STEP 1: Combined scoring
// ============================================================
export function computeFinalForYouScore(listing, signals) {
  const personalization = norm(scoreListingForYou(listing, signals), 15);

  const quality = norm(listing.quality_score ?? 50, 100);

  // Seller trust estimate from embedded data
  const seller = listing.seller || {};
  let trustRaw = 20; // base
  if (seller.is_verified || seller.isVerified || seller.verification?.status === 'verified') trustRaw += 35;
  if (seller.joinedAt || seller.joined_at || listing.created_at) trustRaw += 10;
  const trust = norm(trustRaw, 100);

  // Freshness
  const created = new Date(listing.created_at || 0).getTime();
  const daysSince = (Date.now() - created) / 86400000;
  let freshness = 0.1;
  if (daysSince < 1) freshness = 1;
  else if (daysSince < 3) freshness = 0.85;
  else if (daysSince < 7) freshness = 0.6;
  else if (daysSince < 14) freshness = 0.4;
  else if (daysSince < 30) freshness = 0.2;

  // Trending
  const views = listing.views || 0;
  const contacts = listing.metrics?.contact_clicks || 0;
  const trending = norm(views * 2 + contacts * 4, 200);

  // Penalties
  const spamPenalty = norm(listing.spam_score || 0, 100);
  const scamPenalty = norm(listing._scamRisk || 0, 100);

  const finalScore =
    personalization * 0.35 +
    quality * 0.20 +
    trust * 0.15 +
    freshness * 0.15 +
    trending * 0.10 -
    spamPenalty * 0.20 -
    scamPenalty * 0.20;

  return Math.max(0, finalScore);
}

// ============================================================
// STEP 2: Strict eligibility — PRODUCTION RULES
// ============================================================
// iter316 — Status check is delegated to `isLiveListing` (the same
// helper Trending / Near Me / Top Search now use). The dead-status
// set below stays as documentation of the quality floor; `isLiveListing`
// already rejects every entry in it.

export function isEligibleForForYou(listing) {
  if (!listing) return false;

  // Canonical public-live filter (single source of truth — matches /api/ads).
  if (!isLiveListing(listing)) return false;

  // Quality floor: 40 minimum (raised from 30)
  if ((listing.quality_score ?? 50) < 40) return false;

  // Spam ceiling: block at 50 (tightened from 60)
  if ((listing.spam_score ?? 0) >= 50) return false;

  // Seller trust: block extremely low trust
  const seller = listing.seller || {};
  const isVerified = seller.is_verified || seller.isVerified || seller.verification?.status === 'verified';
  // Don't block unverified sellers entirely, but block flagged/suspended ones
  if (seller.suspended || seller.flagged_for_review) return false;

  // Moderation state: block anything under review
  if (listing.moderation_hold || listing.flagged) return false;

  return true;
}

// ============================================================
// STEP 3: Diversity enforcement
// ============================================================
export function diversifyFeed(rankedItems, maxPerSeller = 2, maxConsecutiveCategory = 3) {
  const result = [];
  const sellerCounts = {};
  const skipped = [];
  let consecutiveSame = 0;
  let lastCategory = null;

  for (const item of rankedItems) {
    const sellerId = item.user_id || 'unknown';
    const category = item.category_id || item.category || '';

    // Seller limit in first 12
    if (result.length < 12) {
      const count = sellerCounts[sellerId] || 0;
      if (count >= maxPerSeller) {
        skipped.push(item);
        continue;
      }
    }

    // Consecutive category limit
    if (category === lastCategory) {
      consecutiveSame++;
      if (consecutiveSame >= maxConsecutiveCategory) {
        skipped.push(item);
        continue;
      }
    } else {
      consecutiveSame = 1;
      lastCategory = category;
    }

    sellerCounts[sellerId] = (sellerCounts[sellerId] || 0) + 1;
    result.push(item);
  }

  // Backfill skipped items at the end
  return [...result, ...skipped];
}

// ============================================================
// STEP 4: Reason tags — ONLY from verified real signals
// ============================================================
export function getForYouReason(listing, signals) {
  const daysSince = (Date.now() - new Date(listing.created_at || 0).getTime()) / 86400000;
  const views = listing.views || 0;
  const city = (listing.city || (typeof listing.location === 'string' ? listing.location : '') || '').toLowerCase();
  const userCity = (signals?.city || '').toLowerCase();
  const seller = listing.seller || {};
  const isVerified = seller.is_verified || seller.isVerified || seller.verification?.status === 'verified';
  const preferredCats = signals?.preferredCategories || [];
  const cat = listing.category_id || listing.category || '';

  // Priority order — only factually earned reasons
  if (daysSince < 1) return { en: 'Recently posted', ar: 'نُشر حديثاً' };
  if (userCity && city && city.includes(userCity)) return { en: 'Near you', ar: 'قريب منك' };
  if (views >= 80) return { en: 'Popular now', ar: 'رائج الآن' };
  if (preferredCats.includes(cat)) return { en: 'Based on your interests', ar: 'بناءً على اهتماماتك' };
  if (isVerified) return { en: 'Trusted seller', ar: 'بائع موثق' };

  // No fake/internal reasons — return null if nothing earned
  return null;
}

// ============================================================
// MAIN: Generate safe "For You" feed
// ============================================================
export function generateSafeForYouFeed(allListings, signals) {
  // 1. Strict eligibility filter
  const eligible = allListings.filter(isEligibleForForYou);

  // 2. Cold start: diverse quality+fresh mix (distinct from Trending)
  if (!hasEnoughSignals(signals)) {
    const coldStart = eligible.map(item => {
      const daysSince = (Date.now() - new Date(item.created_at || 0).getTime()) / 86400000;
      const freshBonus = daysSince < 1 ? 100 : daysSince < 3 ? 70 : daysSince < 7 ? 40 : 10;
      const qualityBonus = (item.quality_score || 50) * 0.5;
      const catHash = (item.category_id || '').charCodeAt(0) || 0;
      const diversityNoise = catHash % 20;

      return {
        ...item,
        _fyFinalScore: freshBonus + qualityBonus + diversityNoise,
        _fyReason: getForYouReason(item, signals),
      };
    });

    coldStart.sort((a, b) => b._fyFinalScore - a._fyFinalScore);
    return diversifyFeed(coldStart);
  }

  // 3. Score all eligible listings with combined formula
  const scored = eligible.map(item => ({
    ...item,
    _fyFinalScore: computeFinalForYouScore(item, signals),
    _fyReason: getForYouReason(item, signals),
  }));

  // 4. Sort by final score, freshness as tiebreaker
  scored.sort((a, b) => {
    if (Math.abs(b._fyFinalScore - a._fyFinalScore) > 0.01) return b._fyFinalScore - a._fyFinalScore;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  // 5. Diversity enforcement
  return diversifyFeed(scored);
}
